import { graphQlClient } from '@config/graphqlClient';
import InvoiceGraphQL from '@graphql/invoice.queries';
import {
  AvailableBudgetItemsResult,
  Invoice,
  InvoiceFilterInput,
  InvoiceLineItemSourceType,
  InvoiceStatus,
  PaginatedInvoicesResult,
} from '@models/invoice.model';

export interface GetPaginatedInvoicesInput {
  page: number;
  limit: number;
  filter?: InvoiceFilterInput;
}

export interface CreateInvoiceDraftInput {
  projectId: string;
  recipientEmail?: string;
}

export interface InvoiceLineItemInput {
  invoiceLineItemId?: string;
  description: string;
  quantity: number;
  unitOfMeasure?: string;
  unitPrice: number;
  taxAmount?: number;
  discountAmount?: number;
  displayOrder?: number;
  sourceType?: InvoiceLineItemSourceType;
  sourceEntityId?: string;
  taskId?: string | null;
  taskName?: string | null;
}

export interface UpdateInvoiceDraftInput {
  invoiceId: string;
  expectedVersion: number;
  recipientEmail?: string;
  taxRate?: number;
  discountRate?: number;
  discountFixedAmount?: number;
  currency?: string;
  paymentTerms?: number;
  orderDescription?: string;
  poNumber?: string;
  lineItems?: InvoiceLineItemInput[];
  taskIds?: string[];
}

export const invoiceService = {
  async getInvoice(invoiceId: string): Promise<Invoice | null> {
    const data: any = await graphQlClient.request(InvoiceGraphQL.queries.getInvoice, { invoiceId });
    return data?.invoice ?? null;
  },

  async getInvoicesByProject(projectId: string, statuses?: InvoiceStatus[]): Promise<Invoice[]> {
    const data: any = await graphQlClient.request(InvoiceGraphQL.queries.getInvoicesByProject, {
      projectId,
      statuses,
    });
    return data?.invoicesByProject ?? [];
  },

  async getPaginatedInvoices(input: GetPaginatedInvoicesInput): Promise<PaginatedInvoicesResult> {
    const data: any = await graphQlClient.request(InvoiceGraphQL.queries.getPaginatedInvoices, {
      input,
    });
    return data?.paginatedInvoices ?? { invoices: [], count: 0 };
  },

  async getAvailableBudgetItems(projectId: string): Promise<AvailableBudgetItemsResult> {
    const data: any = await graphQlClient.request(
      InvoiceGraphQL.queries.getAvailableBudgetItemsForInvoice,
      { projectId },
    );
    return (
      data?.availableBudgetItemsForInvoice ?? {
        materials: [],
        equipment: [],
        expenses: [],
        delegatedTaskCosts: [],
      }
    );
  },

  async createInvoiceDraft(input: CreateInvoiceDraftInput): Promise<Invoice> {
    const data: any = await graphQlClient.request(InvoiceGraphQL.mutations.createInvoiceDraft, {
      input,
    });
    return data?.createInvoiceDraft;
  },

  async updateInvoiceDraft(input: UpdateInvoiceDraftInput): Promise<Invoice> {
    const data: any = await graphQlClient.request(InvoiceGraphQL.mutations.updateInvoiceDraft, {
      input,
    });
    return data?.updateInvoiceDraft;
  },

  async deleteInvoiceDraft(invoiceId: string): Promise<boolean> {
    const data: any = await graphQlClient.request(InvoiceGraphQL.mutations.deleteInvoiceDraft, {
      invoiceId,
    });
    return Boolean(data?.deleteInvoiceDraft);
  },

  async sendInvoice(invoiceId: string): Promise<Invoice> {
    const data: any = await graphQlClient.request(InvoiceGraphQL.mutations.sendInvoice, {
      input: { invoiceId },
    });
    return data?.sendInvoice;
  },

  async voidInvoice(invoiceId: string, reason?: string): Promise<Invoice> {
    const data: any = await graphQlClient.request(InvoiceGraphQL.mutations.voidInvoice, {
      input: { invoiceId, reason },
    });
    return data?.voidInvoice;
  },

  async resendInvoiceEmail(invoiceId: string, recipientEmail?: string): Promise<Invoice> {
    const data: any = await graphQlClient.request(InvoiceGraphQL.mutations.resendInvoiceEmail, {
      input: { invoiceId, recipientEmail },
    });
    return data?.resendInvoiceEmail;
  },

  async refreshInvoiceStatus(invoiceId: string): Promise<Invoice> {
    const data: any = await graphQlClient.request(InvoiceGraphQL.mutations.refreshInvoiceStatus, {
      invoiceId,
    });
    return data?.refreshInvoiceStatus;
  },

  async getTaskInvoicePendingSummary(
    taskId: string,
  ): Promise<{ materialCount: number; equipmentCount: number; sampleNames: string[] }> {
    const data: any = await graphQlClient.request(
      InvoiceGraphQL.queries.taskInvoicePendingSummary,
      { taskId },
    );
    return (
      data?.taskInvoicePendingSummary ?? {
        materialCount: 0,
        equipmentCount: 0,
        sampleNames: [],
      }
    );
  },

  async getProjectInvoicePendingSummary(
    projectId: string,
  ): Promise<{ taskCount: number; materialCount: number; equipmentCount: number; sampleNames: string[] }> {
    const data: any = await graphQlClient.request(
      InvoiceGraphQL.queries.projectInvoicePendingSummary,
      { projectId },
    );
    return (
      data?.projectInvoicePendingSummary ?? {
        taskCount: 0,
        materialCount: 0,
        equipmentCount: 0,
        sampleNames: [],
      }
    );
  },
};
